import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Button, Input, Textarea, Select, Label, EmptyState } from "../components/ui-kit";
import { Plus, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { fmtDateTimeIST } from "../lib/datefmt";

export default function NotificationsPage() {
  const { user } = useAuth();
  const canSend = user?.role === "admin" || user?.role === "manager";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", target_user_id: "" });

  const load = async () => {
    const [n, u] = await Promise.all([api.get("/notifications"), canSend ? api.get("/users") : Promise.resolve({ data: [] })]);
    setRows(n.data);
    setUsers(u.data);
  };
  useEffect(() => {
    load();
    // Initial fetch only; `load` depends on stable `canSend` boolean from auth context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notifications", { title: form.title, message: form.message, target_user_id: form.target_user_id || null });
      toast.success(form.target_user_id ? "Sent to user" : "Sent to all");
      setOpen(false);
      setForm({ title: "", message: "", target_user_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle={canSend ? "Broadcast to your team or DM a specific person" : "Messages from admin & managers"}
        actions={
          canSend && (
            <Button onClick={() => setOpen((s) => !s)} data-testid="new-notif-btn">
              <Plus className="w-4 h-4" /> New notification
            </Button>
          )
        }
      />
      {open && canSend && (
        <Card className="mb-6">
          <form onSubmit={send} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" />
            </div>
            <div className="sm:col-span-2">
              <Label>Message</Label>
              <Textarea required rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="notif-message" />
            </div>
            <div className="sm:col-span-2">
              <Label>Target (leave empty = all users)</Label>
              <Select value={form.target_user_id} onChange={(e) => setForm({ ...form, target_user_id: e.target.value })} data-testid="notif-target">
                <option value="">All users</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email}) — {u.role}
                  </option>
                ))}
              </Select>
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="notif-submit">
                Send
              </Button>
            </div>
          </form>
        </Card>
      )}
      <div className="space-y-3">
        {rows.length === 0 && <EmptyState title="No notifications yet" />}
        {rows.map((n) => (
          <Card key={n.id} className={!n.read ? "border-l-4 border-l-[#E07A5F]" : ""}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#A3B18A]/25 text-[#1E3F20] grid place-items-center flex-shrink-0">
                <Bell className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-heading font-bold text-lg">{n.title}</div>
                  <div className="text-xs text-[#5C6B5E]">{fmtDateTimeIST(n.created_at)}</div>
                </div>
                <p className="text-[#1A2E1C] mt-1 text-sm">{n.message}</p>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-xs text-[#5C6B5E]">
                    from {n.sender_name} · {n.target_user_id ? "direct" : "broadcast"}
                  </div>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="text-xs font-semibold text-[#1E3F20] hover:underline">
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
